import {
  Box,
  Flex,
  Text,
  Input,
  Select,
  Button,
  IconButton,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Divider,
  SimpleGrid,
  Spinner,
  HStack,
  VStack,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import {
  userLookup,
  getUserState,
  creditRequests,
  extendSubscription,
  grantPlan,
  getProducts,
  UserLookupField,
  UserMatch,
  UserState,
  Product,
} from '../../../api/AdminAPI';
import { ADMIN_DASHBOARD_PATH } from 'router/adminPath';

const UserManagement = () => {
  const token = useSelector((state: RootState) => state.userData.apiKey);
  const toast = useToast();
  const navigate = useNavigate();

  // Search
  const [searchField, setSearchField] = useState<UserLookupField>('tg_username');
  const [searchValue, setSearchValue] = useState('');
  const [matches, setMatches] = useState<UserMatch[]>([]);
  const [searching, setSearching] = useState(false);

  // Selected user state
  const [state, setState] = useState<UserState | null>(null);
  const [loadingState, setLoadingState] = useState(false);

  // Forms
  const [creditAmount, setCreditAmount] = useState('');
  const [extendMode, setExtendMode] = useState<'delta' | 'absolute'>('delta');
  const [extendPlanId, setExtendPlanId] = useState('');
  const [extendDays, setExtendDays] = useState('');
  const [extendMonths, setExtendMonths] = useState('');
  const [extendDate, setExtendDate] = useState('');
  const [grantProductId, setGrantProductId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Products for the grant-plan selector (loaded once)
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    if (!token) return;
    let active = true;
    getProducts(token).then((res) => {
      if (active && res.success && res.data) {
        setProducts(res.data.products);
      }
    });
    return () => {
      active = false;
    };
  }, [token]);

  // Live prefix search: after 3 typed chars, debounce and fetch suggestions.
  useEffect(() => {
    const value = searchValue.trim();
    if (!token || value.length < 3) {
      setMatches([]);
      return;
    }
    let active = true;
    const timer = setTimeout(async () => {
      setSearching(true);
      const res = await userLookup(token, searchField, value);
      if (!active) return;
      setSearching(false);
      setMatches(res.success && res.data ? res.data.matches : []);
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [searchValue, searchField, token]);

  const requireToken = (): boolean => {
    if (!token) {
      toast({ title: 'Not authorized', status: 'error', duration: 4000 });
      return false;
    }
    return true;
  };

  const handleSearch = async () => {
    if (!requireToken()) return;
    if (!searchValue.trim()) {
      toast({ title: 'Enter a search value', status: 'warning', duration: 3000 });
      return;
    }
    setSearching(true);
    setMatches([]);
    setState(null);
    const res = await userLookup(token, searchField, searchValue.trim());
    setSearching(false);
    if (res.success && res.data) {
      setMatches(res.data.matches);
      if (res.data.matches.length === 0) {
        toast({ title: 'No users found', status: 'info', duration: 3000 });
      }
    } else {
      toast({ title: res.error || 'Lookup failed', status: 'error', duration: 4000 });
    }
  };

  const loadState = async (userId: number) => {
    if (!requireToken()) return;
    setLoadingState(true);
    const res = await getUserState(token, userId);
    setLoadingState(false);
    if (res.success && res.data) {
      setState(res.data);
      // Default the extend target to the prioritized (first non-fallback) plan,
      // falling back to the first plan if the user has only fallback/one plan.
      const preferred =
        res.data.plans.find((p) => !p.is_fallback) ?? res.data.plans[0];
      setExtendPlanId(preferred ? String(preferred.id) : '');
    } else {
      toast({ title: res.error || 'Failed to load state', status: 'error', duration: 4000 });
    }
  };

  const afterMutation = async (title: string, userId: number, updated?: UserState) => {
    if (updated) {
      setState(updated);
    } else {
      await loadState(userId);
    }
    toast({ title, status: 'success', duration: 4000 });
  };

  const handleCredit = async () => {
    if (!requireToken() || !state) return;
    const amount = parseInt(creditAmount, 10);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast({ title: 'Amount must be > 0', status: 'warning', duration: 3000 });
      return;
    }
    setSubmitting(true);
    const res = await creditRequests(token, state.user_id, amount);
    setSubmitting(false);
    if (res.success) {
      setCreditAmount('');
      await loadState(state.user_id);
      toast({
        title: `Credited +${amount}. Remaining: ${res.data?.total_remaining ?? '?'}`,
        status: 'success',
        duration: 4000,
      });
    } else {
      toast({ title: res.error || 'Credit failed', status: 'error', duration: 4000 });
    }
  };

  const handleExtend = async () => {
    if (!requireToken() || !state) return;
    if (state.plans.length === 0) {
      toast({ title: 'User has no plan to extend', status: 'warning', duration: 3000 });
      return;
    }
    if (!extendPlanId) {
      toast({ title: 'Pick a subscription to extend', status: 'warning', duration: 3000 });
      return;
    }
    const planId = parseInt(extendPlanId, 10);
    setSubmitting(true);
    let res;
    if (extendMode === 'delta') {
      const days = extendDays ? parseInt(extendDays, 10) : 0;
      const months = extendMonths ? parseInt(extendMonths, 10) : 0;
      if (days <= 0 && months <= 0) {
        setSubmitting(false);
        toast({ title: 'Enter a positive days/months shift', status: 'warning', duration: 3000 });
        return;
      }
      res = await extendSubscription(token, {
        user_id: state.user_id,
        mode: 'delta',
        days,
        months,
        plan_id: planId,
      });
    } else {
      if (!extendDate) {
        setSubmitting(false);
        toast({ title: 'Pick a date', status: 'warning', duration: 3000 });
        return;
      }
      res = await extendSubscription(token, {
        user_id: state.user_id,
        mode: 'absolute',
        date_end: extendDate,
        plan_id: planId,
      });
    }
    setSubmitting(false);
    if (res.success) {
      setExtendDays('');
      setExtendMonths('');
      setExtendDate('');
      await afterMutation('Subscription updated', state.user_id, res.data);
    } else {
      toast({ title: res.error || 'Extend failed', status: 'error', duration: 4000 });
    }
  };

  const handleGrant = async () => {
    if (!requireToken() || !state) return;
    const productId = parseInt(grantProductId, 10);
    if (!Number.isFinite(productId) || productId <= 0) {
      toast({ title: 'Select a product', status: 'warning', duration: 3000 });
      return;
    }
    setSubmitting(true);
    const res = await grantPlan(token, state.user_id, productId);
    setSubmitting(false);
    if (res.success) {
      setGrantProductId('');
      await afterMutation('Plan granted', state.user_id, res.data);
    } else {
      toast({ title: res.error || 'Grant failed', status: 'error', duration: 4000 });
    }
  };

  const productLabel = (p: Product): string => {
    const name = p.product_name_en || p.product_name_ru || `Product ${p.id}`;
    const reqs = p.add_requests != null ? ` — ${p.add_requests} req` : '';
    return `#${p.id} ${name}${reqs}`;
  };

  const planLabel = (planId: number): string => {
    const p = state?.plans.find((x) => x.id === planId);
    if (!p) return `Plan ${planId}`;
    const name = p.product_name || `product ${p.product_id}`;
    return `#${p.id} ${name} — ends ${p.date_end || '-'} (${p.status})`;
  };

  // Action forms — shared block, rendered above the plans history.
  const actionForms = state && (
    <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={5}>
      {/* Credit requests */}
      <Box borderWidth="1px" borderRadius="md" p={4}>
        <Text fontWeight="semibold" mb={3}>
          Credit requests (+N)
        </Text>
        <VStack align="stretch" spacing={3}>
          <Input
            type="number"
            placeholder="Amount"
            value={creditAmount}
            onChange={(e) => setCreditAmount(e.target.value)}
          />
          <Button colorScheme="green" onClick={handleCredit} isLoading={submitting}>
            Credit
          </Button>
        </VStack>
      </Box>

      {/* Extend / set date */}
      <Box borderWidth="1px" borderRadius="md" p={4}>
        <Text fontWeight="semibold" mb={3}>
          Extend / set date
        </Text>
        <VStack align="stretch" spacing={3}>
          {state.plans.length === 0 ? (
            <Text color="text.secondary" fontSize="sm">
              User has no subscription to extend — grant a plan first.
            </Text>
          ) : (
            <>
              <Select
                value={extendPlanId}
                onChange={(e) => setExtendPlanId(e.target.value)}
              >
                {state.plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {planLabel(p.id)}
                  </option>
                ))}
              </Select>
              <Select
                value={extendMode}
                onChange={(e) => setExtendMode(e.target.value as 'delta' | 'absolute')}
              >
                <option value="delta">Shift (delta)</option>
                <option value="absolute">Set exact date</option>
              </Select>
              {extendMode === 'delta' ? (
                <HStack>
                  <Input
                    type="number"
                    placeholder="+days"
                    value={extendDays}
                    onChange={(e) => setExtendDays(e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="+months"
                    value={extendMonths}
                    onChange={(e) => setExtendMonths(e.target.value)}
                  />
                </HStack>
              ) : (
                <Input
                  type="date"
                  value={extendDate}
                  onChange={(e) => setExtendDate(e.target.value)}
                />
              )}
              <Button colorScheme="orange" onClick={handleExtend} isLoading={submitting}>
                Apply
              </Button>
            </>
          )}
        </VStack>
      </Box>

      {/* Grant plan */}
      <Box borderWidth="1px" borderRadius="md" p={4}>
        <Text fontWeight="semibold" mb={3}>
          Grant product-plan
        </Text>
        <VStack align="stretch" spacing={3}>
          <Select
            placeholder="Select a product"
            value={grantProductId}
            onChange={(e) => setGrantProductId(e.target.value)}
          >
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {productLabel(p)}
              </option>
            ))}
          </Select>
          <Button colorScheme="purple" onClick={handleGrant} isLoading={submitting}>
            Grant
          </Button>
        </VStack>
      </Box>
    </SimpleGrid>
  );

  return (
    <Box p={{ base: 4, md: 6 }}>
      <Flex align="center" gap={4} mb={6}>
        <IconButton
          aria-label="Back"
          icon={<FaArrowLeft />}
          onClick={() => navigate(ADMIN_DASHBOARD_PATH)}
          variant="ghost"
          size="lg"
        />
        <Box>
          <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold">
            User Management
          </Text>
          <Text color="text.secondary" fontSize={{ base: 'sm', md: 'md' }}>
            Look up a user and manage requests / subscription
          </Text>
        </Box>
      </Flex>

      {/* Search */}
      <Box borderWidth="1px" borderRadius="md" p={4} mb={6}>
        <Text fontWeight="semibold" mb={3}>
          Search user
        </Text>
        <Flex gap={3} flexDir={{ base: 'column', md: 'row' }}>
          <Select
            maxW={{ md: '220px' }}
            value={searchField}
            onChange={(e) => setSearchField(e.target.value as UserLookupField)}
          >
            <option value="tg_username">Telegram username</option>
            <option value="email">Email</option>
            <option value="user_id">User ID</option>
            <option value="username">Web username</option>
          </Select>
          <Input
            placeholder="Type 3+ chars to search…"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button colorScheme="blue" onClick={handleSearch} isLoading={searching} minW="120px">
            Search
          </Button>
        </Flex>

        {matches.length > 0 && (
          <Box mt={4} overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>User ID</Th>
                  <Th>Email</Th>
                  <Th>Web username</Th>
                  <Th>TG username</Th>
                  <Th>TG ID</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {matches.map((m) => (
                  <Tr key={`${m.user_id}-${m.telegram_user_id ?? 'none'}`}>
                    <Td>{m.user_id}</Td>
                    <Td>{m.email || '-'}</Td>
                    <Td>{m.username || '-'}</Td>
                    <Td>{m.tg_username || '-'}</Td>
                    <Td>{m.telegram_user_id ?? '-'}</Td>
                    <Td>
                      <Button size="xs" onClick={() => loadState(m.user_id)}>
                        Select
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>

      {loadingState && (
        <Flex justify="center" py={8}>
          <Spinner />
        </Flex>
      )}

      {/* State card */}
      {state && !loadingState && (
        <Box borderWidth="1px" borderRadius="md" p={4} mb={6}>
          <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
            <Text fontWeight="bold" fontSize="lg">
              User #{state.user_id} {state.username ? `(${state.username})` : ''}
            </Text>
            <Button size="xs" variant="outline" onClick={() => loadState(state.user_id)}>
              Reload
            </Button>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={4}>
            <Box>
              <Text fontSize="sm" color="text.secondary">
                Email
              </Text>
              <Text>{state.email || '-'}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="text.secondary">
                Telegram
              </Text>
              <Text>
                {state.telegram
                  ? `@${state.telegram.username ?? '?'} (${state.telegram.telegram_user_id})`
                  : '-'}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="text.secondary">
                Balance
              </Text>
              <Text>${state.balance.toFixed(2)}</Text>
            </Box>
          </SimpleGrid>

          <Text fontWeight="semibold" mb={3}>
            Total remaining (active): {state.total_remaining}
          </Text>

          {/* Action forms — above the balance/plans history */}
          {actionForms}

          <Divider my={5} />

          {/* Plans / balance history */}
          <Text fontWeight="semibold" mb={2}>
            Plans history
          </Text>
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Plan</Th>
                  <Th>Product</Th>
                  <Th>Requests left</Th>
                  <Th>Start</Th>
                  <Th>End</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {state.plans.map((p) => (
                  <Tr key={p.id}>
                    <Td>{p.id}</Td>
                    <Td>
                      {p.product_name || p.product_id}
                      {p.is_fallback ? ' (fallback)' : ''}
                    </Td>
                    <Td>{p.requests_left}</Td>
                    <Td>{p.date_start || '-'}</Td>
                    <Td>{p.date_end || '-'}</Td>
                    <Td>
                      <Badge
                        colorScheme={
                          p.status === 'active'
                            ? 'green'
                            : p.status === 'fallback'
                            ? 'gray'
                            : 'red'
                        }
                      >
                        {p.status}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
                {state.plans.length === 0 && (
                  <Tr>
                    <Td colSpan={6}>
                      <Text color="text.secondary">No plans</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UserManagement;
